/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, type ReactElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CloseIcon, SearchIcon } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import SearchDropdown from '@/components/search/search-dropdown';
import RecentSearches from '@/components/search/recent-searches';
import { Spinner } from '@/components/spinner';
import { useSearchPreview } from '@/hooks/use-search-preview';
import { SEARCH_PREVIEW_MIN_LENGTH } from '@/lib/search/types';
import { searchUrlBuilder } from '@/lib/url';
import { clearSessionJSONItem, cn, getSessionJSONItem, setSessionJSONItem } from '@/lib/utils';

import { UITarget } from '@/targets/ui-target';

const RECENT_SEARCH_LIMIT = 5;
const RECENT_SEARCH_KEY = 'recent-search-key';

const readRecentSearches = () => getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) ?? [];

export default function SearchBar(): ReactElement {
    const { t } = useTranslation('header');
    const { t: tCommon } = useTranslation('common');
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    // Recent searches submitted in this session (sessionStorage, like the template default). Read on focus only, so
    // the server render and hydration never touch storage.
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    // Set when Escape closes the panel so the focus we return to the input does not immediately
    // reopen it via the input's onFocus handler. Cleared on the next real focus.
    const suppressReopenRef = useRef(false);
    const searchInputId = `header-search-input-${useId()}`;
    const dropdownId = `header-search-dropdown-${useId()}`;

    const { data: preview, isLoading } = useSearchPreview(query);
    const term = query.trim();
    // CA1: nothing is shown below the minimum length; CA2/CA3: the panel follows the latest preview and shows a
    // spinner while the preview for the current query is on its way.
    const showDropdown = open && term.length >= SEARCH_PREVIEW_MIN_LENGTH && (Boolean(preview) || isLoading);
    // Recent below the minimum length the panel shows the saved searches instead, when there are any.
    const showRecentSearches = open && term.length < SEARCH_PREVIEW_MIN_LENGTH && recentSearches.length > 0;
    const showPanel = showDropdown || showRecentSearches;

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
        setOpen(true);
    }, []);

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            if (!term) return;
            const searches = [
                term,
                ...readRecentSearches().filter((saved) => saved.toLowerCase() !== term.toLowerCase()),
            ].slice(0, RECENT_SEARCH_LIMIT);
            setSessionJSONItem(RECENT_SEARCH_KEY, searches);
            setRecentSearches(searches);
            setOpen(false);
            void navigate(searchUrlBuilder(term), { state: { query: term } });
        },
        [navigate, term]
    );

    const handleFocus = useCallback(() => {
        if (suppressReopenRef.current) {
            suppressReopenRef.current = false;
            return;
        }
        setRecentSearches(readRecentSearches());
        setOpen(true);
    }, []);

    const handleClear = useCallback(() => {
        setQuery('');
        setOpen(false);
        inputRef.current?.focus();
    }, []);

    const handleNavigate = useCallback(() => {
        setOpen(false);
        inputRef.current?.blur();
    }, []);

    const handleRecentSearch = useCallback(
        (link: string) => {
            handleNavigate();
            void navigate(link);
        },
        [handleNavigate, navigate]
    );

    const handleClearRecentSearches = useCallback(() => {
        clearSessionJSONItem(RECENT_SEARCH_KEY);
        setRecentSearches([]);
        inputRef.current?.focus();
    }, []);

    const handleRemoveRecentSearch = useCallback((search: string) => {
        const searches = readRecentSearches().filter((saved) => saved !== search);
        if (searches.length > 0) {
            setSessionJSONItem(RECENT_SEARCH_KEY, searches);
        } else {
            clearSessionJSONItem(RECENT_SEARCH_KEY);
        }
        setRecentSearches(searches);
        // The removed entry's button unmounts with focus on it; move focus back so the panel keeps closing on blur.
        inputRef.current?.focus();
    }, []);

    const handleContainerBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
        // Close the panel only when focus leaves the whole search widget, so keyboard users can Tab
        // from the input into the dropdown links (WCAG 2.1.1).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setOpen(false);
        }
    }, []);

    // Escape closes the panel and returns focus to the input. Attached as a native keydown listener
    // so the wrapper keeps no interactive role while still catching Escape from any descendant.
    useEffect(() => {
        const node = containerRef.current;
        if (!node) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                // Only arm the flag when focus actually moves into the input; if the input already has
                // focus, .focus() is a no-op, onFocus never runs, and the flag would stay stuck.
                if (document.activeElement !== inputRef.current) {
                    suppressReopenRef.current = true;
                }
                setOpen(false);
                inputRef.current?.focus();
            }
        };
        node.addEventListener('keydown', onKeyDown);
        return () => node.removeEventListener('keydown', onKeyDown);
    }, []);

    return (
        <UITarget targetId="sfcc.header.search.input">
            <div ref={containerRef} className="relative" onBlur={handleContainerBlur}>
                <form onSubmit={handleSubmit} className="relative z-10" role="search">
                    <div className="relative flex items-center [--ui-radius:var(--radius-lg)]">
                        <label htmlFor={searchInputId} className="sr-only">
                            {t('searchPlaceholder')}
                        </label>
                        <div className="relative min-w-0 flex-1">
                            <Input
                                ref={inputRef}
                                id={searchInputId}
                                type="text"
                                value={query}
                                placeholder={t('searchPlaceholder')}
                                className={cn(
                                    'h-10 w-full rounded-r-none border-r-0 border-header-search-border px-3.5 text-xs text-ps-text-label placeholder:text-header-search-placeholder-mobile lg:text-sm lg:placeholder:text-header-search-placeholder focus-visible:border-header-search-button focus-visible:ring-1 focus-visible:ring-header-search-button',
                                    query && 'pr-10'
                                )}
                                onChange={handleInputChange}
                                onFocus={handleFocus}
                                aria-autocomplete="list"
                                aria-expanded={showPanel}
                                aria-controls={dropdownId}
                                aria-haspopup="dialog"
                                role="combobox"
                                autoComplete="off"
                                data-testid="header-search"
                            />
                            {query && (
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="absolute right-0 top-1/2 flex -translate-y-1/2 cursor-pointer items-center justify-center rounded-full p-2 text-ps-text-label hover:bg-ps-hover-primary"
                                    aria-label={t('searchClear')}
                                    data-testid="header-search-clear">
                                    <CloseIcon className="size-5" />
                                </button>
                            )}
                        </div>
                        <Button
                            type="submit"
                            className="h-10 w-12 lg:w-15 shrink-0 rounded-l-none bg-header-search-button text-header-search-button-foreground hover:bg-header-search-button/90 cursor-pointer"
                            aria-label={t('searchSubmit')}>
                            <SearchIcon className="size-5 text-header-search-button-foreground!" />
                        </Button>
                    </div>
                </form>
                {showPanel && (
                    <div
                        id={dropdownId}
                        className="absolute left-0 top-full z-50 w-full overflow-y-auto rounded-b-lg bg-background drop-shadow-md md:w-max md:min-w-full md:max-w-[calc(100vw-4rem)]"
                        // Keep the panel within the viewport below the header so its own scroll can reach every item.
                        style={{ maxHeight: 'calc(100dvh - var(--header-height, 0px))' }}
                        role="dialog"
                        aria-label={t('searchSuggestions')}
                        aria-busy={showDropdown && isLoading}>
                        {showRecentSearches && (
                            <RecentSearches
                                recentSearches={recentSearches}
                                closeAndNavigate={handleRecentSearch}
                                clearRecentSearches={handleClearRecentSearches}
                                removeRecentSearch={handleRemoveRecentSearch}
                            />
                        )}
                        {showDropdown && isLoading && (
                            <div
                                role="status"
                                data-testid="header-search-loading"
                                className={cn(
                                    'flex items-center justify-center',
                                    preview ? 'absolute right-4 top-4 z-10' : 'px-4 py-10'
                                )}>
                                <Spinner size="sm" />
                                <span className="sr-only">{tCommon('loading')}</span>
                            </div>
                        )}
                        {showDropdown && preview && (
                            <div className={cn('transition-opacity', isLoading && 'pointer-events-none opacity-60')}>
                                <SearchDropdown preview={preview} query={query} onNavigate={handleNavigate} />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </UITarget>
    );
}
